import Vue from 'vue';
import api from "@modules/JourneyBuilder/api";

export default {
    namespaced: true,
    state: {
        /**
         * @type {string|undefined} Id of the currently edited journey
         */
        journeyId: undefined,
        
        //Initialise permission values with NULL to ensure our watch functions get triggered
        //when the actual value is set

        /**
         * @type {Boolean|null}
         */
        hasWritePermission: null,
        
        /**
         * @type {string[]} Collection of brick ids with a menu currently displayed
         */
        menuShownBrickIds: [],

        /**
         * @type {JourneyBrickCommentIndicator[]} Array of all brick comment indicators
         */
        brickCommentIndicators: [],
        
        /**
         * @type {Object.<String, JourneyComment[]>} Map between all brickId -> comments
         */
        journeyComments: {},

        /**
         * @type {String[]} Collection of all bricks currently loading comments
         */
        journeyComponentsLoadingComments: [],

        /**
         * @type {Number[]} Collection of all comments currently marked as publishing
         */
        publishingCommentIds: [],

        /**
         * @type {Object.<string, string>} Dictionary containing a mapping between brickId/comment  
         */
        newComments: {},
        
        /**
         *  @type {Object.<String, { id: String, type: Number, isNew: Boolean }>} Object containing keyValuePairs of brickId/brick
         */
        brickMap: {},

        /**
         * @type {*[]} Array containing all available connection conditions
         */
        availableConnectionConditions: [],

        /**
         * @type {Boolean} bit indicating whether the first render cycle is finished or not, certain functions may be batched to speed up initial render
         */
        finishedInitialRenderCycle: false,

        /**
         * @type {Boolean} bit indicating whether to show the journey filters editor or not
         */
        showJourneyFiltersEditor: false,

        /**
         * @type {Boolean} bit indicating whether or not filter exclusion checkpoints are to be focussed
         */
        filterExclusionFocusMode: false
    },
    getters: {
        menuShown(state){
            return brickId => state.menuShownBrickIds.includes(brickId);
        },
        hasComments(state){
            return brickId => {
                const indicator = state.brickCommentIndicators.find(ci => ci.brickId === brickId);
                return indicator && indicator.hasComments;
            }
        },
        hasUnseenComments(state){
            return brickId => {
                const indicator = state.brickCommentIndicators.find(ci => ci.brickId === brickId);
                return indicator && indicator.hasUnseenComments;
            }
        },
        isLoadingComments(state){
            return brickId => state.journeyComponentsLoadingComments.includes(brickId);
        },
        isCommentPublishing(state){
            return commentId => state.publishingCommentIds.includes(commentId);
        }
    },
    mutations: {
        /**
         * Set the currently edited journey id
         * @param state
         * @param {string} journeyId
         */
        setJourneyId(state, journeyId){
            state.journeyId = journeyId;
        },
        /**
         * Set whether the current user has write permissions or not
         * @param {Object} state
         * @param {boolean} hasWritePermission
         */
        setHasWritePermission(state, hasWritePermission){
            state.hasWritePermission = hasWritePermission;
        },
        /**
         * Empty the brickMap
         * @param state 
         */
        clearBrickMap(state) {
            state.brickMap = {};
        },
        /**
         * Add a brick to the bricks
         * @param state 
         * @param {JourneyBrick} brick 
         */
        addBrickToMap(state, brick) {
            //For now, to keep it simple we are only taking the properties we need.
            Vue.set(state.brickMap, brick.id, { 
                id: brick.id, 
                type: brick.type,
                isNew: brick.isNew
            });
        },
        /**
         * Update the given property for the given brick in the brickMap
         * @param state
         * @param {{ brickId: String, property: String, value: * }} parameters
         */
        updateBrickMapProperty(state, parameters){
            Vue.set(state.brickMap[parameters.brickId], parameters.property, parameters.value);
        },
        /**
         * Remove a brick from bricks
         * @param state 
         * @param {String} id 
         */
        removeBrickFromMap(state, id) {
            Vue.delete(state.brickMap, id);
        },
        /**
         * Toggle whether the given brick menu is shown or not
         * @param state
         * @param {{ brickId: String, shown: boolean }} parameters
         */
        toggleMenuShown(state, parameters){
            if(parameters.shown) {
                state.menuShownBrickIds.push(parameters.brickId);
            } else {
                const idx = state.menuShownBrickIds.indexOf(parameters.brickId);

                if(idx !== -1){
                    state.menuShownBrickIds.splice(idx, 1);
                }
            }
        },
        /**
         * Create/update a new comment for the given brickId
         * @param state
         * @param {{ brickId: String, content: String } | { connectionId: String, checkpoint: String, content: String }} parameters
         */
        setNewComment(state, parameters){
            Vue.set(state.newComments, parameters.brickId, parameters.content);
        },
        /**
         * Reset comment state
         * @param state
         */
        resetComments(state){
            state.newComments = {};
            state.journeyComments = {};
            state.brickCommentIndicators = [];
        },
        /**
         * Set brick comment indicators
         * @param state
         * @param {JourneyBrickCommentIndicator[]} indicators
         */
        setBrickCommentIndicators(state, indicators){
            state.brickCommentIndicators = indicators;
        },
        /**
         * Set the comments state field
         * @param state
         * @param {{ relatedId: String, comments: JourneyComment[] }} parameters
         */
        setJourneyComments(state, parameters){
            Vue.set(state.journeyComments, parameters.relatedId, parameters.comments);
        },
        /**
         * Set the content for a specific comment
         * @param state
         * @param {{ relatedId: String, commentId: Number, content: String }} parameters
         */
        setCommentContent(state, parameters){
            const comment = state.journeyComments[parameters.relatedId]
                .find(c => c.id === parameters.commentId);
            comment.content = parameters.content;
        },
        /**
         * Mark the given journey component as loading comments
         * @param state
         * @param {String} relatedId
         */
        markJourneyComponentLoadingComments(state, relatedId){
            state.journeyComponentsLoadingComments.push(relatedId);
        },
        /**
         * Mark the given journey component as done loading comments
         * @param state
         * @param {String} relatedId
         */
        markJourneyComponentLoadingCommentsDone(state, relatedId){
            const idx = state.journeyComponentsLoadingComments.indexOf(relatedId);
            state.journeyComponentsLoadingComments.splice(idx, 1);
        },
        /**
         * Mark the comment with the given id as publishing new content
         * @param state
         * @param {Number} commentId
         */
        markCommentPublishing(state, commentId){
            state.publishingCommentIds.push(commentId);
        },
        /**
         * Mark the comment with the given id as published new content
         * @param state
         * @param {Number} commentId
         */
        markCommentPublishDone(state, commentId){
            const idx = state.publishingCommentIds.indexOf(commentId);
            state.publishingCommentIds.splice(idx, 1);
        },
        /**
         * Mark the initial render cycle as finished, allowing components to skip batching for costly operations
         * @param state
         */
        markInitialRenderCycleFinished(state){
            state.finishedInitialRenderCycle = true;
        },
        setAvailableConnectionConditions(state, availableConditions){
            state.availableConnectionConditions = availableConditions;
        },
        toggleJourneyFiltersEditor(state, show){
            state.showJourneyFiltersEditor = show;
        },
        toggleFilterExclusionFocusMode(state, enabled){
            state.filterExclusionFocusMode = enabled;
        }
    },
    actions: {
        async loadPermissions({state, commit}){
            let hasPermission = false;
            
            try {
                await api.validateWritePermission();
                hasPermission = true;
            } catch(e){
                hasPermission = false;
            }
            
            //Our mutation should only be invoked once
            if(state.hasWritePermission === hasPermission){
                return;
            }
            
            commit('setHasWritePermission', hasPermission);
        },
        
        /**
         * Call addBrickToMap for all provided bricks.
         * @param {Function} commit Commit vuex state.
         * @param {Array} bricks
         */
        setBrickMap({commit}, bricks) {

            commit('clearBrickMap');

            bricks.forEach(brick => {
                commit('addBrickToMap', brick);
            });
        },
        /**
         * Update the given comment with the given text
         * @param commit
         * @param state
         * @param {{ relatedId: String, commentId: String, content: String }} parameters
         * @return {Promise<void>}
         */
        async updateComment({commit, state}, parameters){
            //* Update comment content locally
            //* Invoke API -> Update content
            //* On success: Do nothing
            //* On error: Revert content?

            const {content: currentContent} = state.journeyComments[parameters.relatedId]
                .find(comment => comment.id === parameters.commentId);
            
            //Safeguard against duplicate content
            if(currentContent === parameters.content){
                return;
            }
            
            commit('setCommentContent', parameters);
            
            //Always display our updated content until:
            //* Our update fails
            //* Our update succeeds
            commit('markCommentPublishing', parameters.commentId);
            
            try {
                await api.updateComment({
                    id: parameters.commentId,
                    content: parameters.content
                });
                
            } catch (e) {
                //Our update failed; revert our (displayed) content back to original
                commit('setCommentContent', {
                    ...parameters,
                    content: currentContent
                });
                
                throw e;
            } finally {
                commit('markCommentPublishDone', parameters.commentId);
            }
        },
        /**
         * Load the comments for the given journey component
         * @param commit
         * @param state
         * @param {{ relatedId: String, type: "Brick" | "Connection" }} parameters
         * @return {Promise<void>}
         */
        async loadJourneyComments({state, commit}, parameters){
            const relatedId = parameters.relatedId;
            const type = parameters.type;
            
            if(state.journeyComponentsLoadingComments.includes(relatedId) || state.journeyComments[relatedId]){
                return;
            }
            
            commit(
                'markJourneyComponentLoadingComments',
                relatedId
            );
            
            try {
                const comments = await api.getComments(relatedId, type);

                commit(
                    'setJourneyComments',
                    {
                        relatedId,
                        comments
                    }
                );
            } finally {
                commit('markJourneyComponentLoadingCommentsDone', relatedId);
            }
        },
        async loadBrickCommentIndicators({commit}, journeyId){
            commit(
                'setBrickCommentIndicators', 
                await api.getBrickCommentIndicators(journeyId)
            );
        },
        async loadAvailableConnectionConditions({commit}){
            commit('setAvailableConnectionConditions', await api.getJourneyConditions());
        }
    }
};
